import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '../lib/api';
import { OnboardingTarget } from '../components/onboarding';
import { useOnboarding, ONBOARDING_TOOLTIPS } from '../hooks/useOnboarding';

interface UserStats {
  sessionsThisMonth: number;
  totalSessions: number;
  avgCoverage: number;
  recentSessions: Array<{
    id: string;
    title: string;
    coverageScore: number;
    startedAt: string;
    itemsCovered: number;
    itemsTotal: number;
  }>;
}

interface SubscriptionInfo {
  type: 'individual' | 'organization';
  tier: string;
  status: string;
  seatCount?: number;
  maxSeats?: number;
}

export function Dashboard() {
  const { user } = useUser();
  const { get } = useApi();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Onboarding
  const { shouldShowTooltip, dismissTooltip, skipAll, isLastTooltip } = useOnboarding();
  const showDashboardTip = shouldShowTooltip(ONBOARDING_TOOLTIPS.DASHBOARD);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [userResponse, subResponse] = await Promise.all([
          get<{ user: any; stats: UserStats }>('/users/me'),
          get<SubscriptionInfo>('/billing/subscription'),
        ]);
        setStats(userResponse.stats);
        setSubscription(subResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome header with onboarding tooltip */}
      <OnboardingTarget
        tooltipId="dashboard"
        tooltipTitle="Your Dashboard"
        tooltipDescription="This is your home base. Track your session stats, view recent demos, and quickly start a new session."
        tooltipPosition="bottom"
        show={showDashboardTip}
        onDismiss={() => dismissTooltip(ONBOARDING_TOOLTIPS.DASHBOARD)}
        onSkipAll={skipAll}
        isLast={isLastTooltip(ONBOARDING_TOOLTIPS.DASHBOARD)}
      >
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {user?.firstName || 'Demo Presenter'}
          </h1>
          <p className="mt-1 text-slate-400">
            Here's an overview of your demo performance
          </p>
        </div>
      </OnboardingTarget>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Sessions This Month"
          value={stats?.sessionsThisMonth ?? 0}
          subtitle={subscription?.tier === 'free' ? 'of 5 free sessions' : 'unlimited'}
          color="green"
        />
        <StatCard
          title="Total Sessions"
          value={stats?.totalSessions ?? 0}
          subtitle="all time"
          color="blue"
        />
        <StatCard
          title="Average Coverage"
          value={`${(stats?.avgCoverage ?? 0).toFixed(0)}%`}
          subtitle="talking points covered"
          color="purple"
        />
      </div>

      {/* Subscription info */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-white">Your Plan</h2>
            <p className="text-slate-400">
              {subscription?.tier === 'free' ? (
                'Free tier - 5 sessions per month'
              ) : subscription?.tier === 'individual' ? (
                'Individual - Unlimited sessions, no history'
              ) : (
                `Team - ${subscription?.seatCount || 0} of ${subscription?.maxSeats || 0} seats`
              )}
            </p>
          </div>
          <Link
            to="/settings/billing"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            {subscription?.tier === 'free' ? 'Upgrade' : 'Manage'}
          </Link>
        </div>
      </div>

      {/* Recent sessions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-white">Recent Sessions</h2>
          <Link
            to="/sessions"
            className="text-sm text-green-500 hover:text-green-400"
          >
            View all
          </Link>
        </div>

        {stats?.recentSessions && stats.recentSessions.length > 0 ? (
          <div className="space-y-3">
            {stats.recentSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
            <p className="text-slate-400">No sessions yet</p>
            <Link
              to="/app"
              className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
            >
              Start your first session
            </Link>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/app"
          className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg p-6 hover:from-green-500 hover:to-green-600 transition-all"
        >
          <h3 className="text-lg font-medium text-white">Start New Session</h3>
          <p className="mt-1 text-green-100/80 text-sm">
            Practice your demo with real-time tracking
          </p>
        </Link>
        <Link
          to="/templates"
          className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-all"
        >
          <h3 className="text-lg font-medium text-white">Manage Templates</h3>
          <p className="mt-1 text-slate-400 text-sm">
            Create and organize your agenda templates
          </p>
        </Link>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  color,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  color: 'green' | 'blue' | 'purple';
}) {
  const colorClasses = {
    green: 'text-green-500',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <p className="text-sm font-medium text-slate-400">{title}</p>
      <p className={`mt-2 text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

function SessionCard({
  session,
}: {
  session: {
    id: string;
    title: string;
    coverageScore: number;
    startedAt: string;
    itemsCovered: number;
    itemsTotal: number;
  };
}) {
  const date = new Date(session.startedAt);
  const coverageColor =
    session.coverageScore >= 80
      ? 'text-green-500'
      : session.coverageScore >= 50
      ? 'text-yellow-500'
      : 'text-red-500';

  return (
    <Link
      to={`/sessions/${session.id}`}
      className="block bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-all"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium">{session.title || 'Untitled Session'}</h3>
          <p className="text-sm text-slate-400">
            {date.toLocaleDateString()} at {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-bold ${coverageColor}`}>
            {session.coverageScore?.toFixed(0) || 0}%
          </p>
          <p className="text-xs text-slate-500">
            {session.itemsCovered}/{session.itemsTotal} covered
          </p>
        </div>
      </div>
    </Link>
  );
}
